// /tiller/unmapped — standalone view of Tiller accounts seen in past uploads
// but not yet mapped to any FRT account. Pulls from the aggregated
// /tiller/unmapped endpoint (dedupes across uploads, excludes already-linked).
//
// Each row offers an inline "Link to" picker. Selecting an FRT account and
// hitting Save calls /accounts/:id/link-tiller, which appends a
// tiller_mapping_history entry with reason='initial_link'.
//
// The picker prioritizes app accounts whose account_number_last4 matches
// the Tiller row's last4 — those float to the top with a "(last4 match)"
// badge so admins can pattern-match without scanning the whole list.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { accounts as accountsApi, ApiError, accounts, tillerUnmapped } from '@/lib/api';
import { formatBalance } from '@/lib/format';
import { useMe } from '@/hooks/useMe';
import type { AccountListItem, UnmappedTillerAccount } from '@frt/shared-types';

export function TillerUnmappedPage() {
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';

  const unmapped = useQuery({
    queryKey: ['tiller', 'unmapped'],
    queryFn: () => tillerUnmapped.list(),
  });

  // App accounts pool — used to populate the per-row "Link to" picker.
  // 500 is the API ceiling; if your installation outgrows that, server-side
  // filtering in the picker would be the next step.
  const appAccounts = useQuery({
    queryKey: ['accounts', { per_page: 500 }],
    queryFn: () => accounts.list({ per_page: 500 }),
    enabled: canEdit,
  });

  const rows = unmapped.data?.data ?? [];
  const accountsList = appAccounts.data?.data ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Unmapped Tiller Accounts</h1>
      <p className="text-sm text-slate-600">
        Tiller accounts seen in past uploads that aren't yet linked to any FRT account.
        Use this view to clean up accumulated unmatched rows outside the upload-time
        Resolutions modal.
      </p>

      <Card>
        <CardHeader>
          <CardTitle>{rows.length} unmapped</CardTitle>
        </CardHeader>
        <CardBody>
          {unmapped.isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : unmapped.error ? (
            <div className="text-sm text-red-600">{(unmapped.error as Error).message}</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-500">
              All Tiller accounts seen in past uploads are linked.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-3">Tiller account</th>
                    <th className="py-1 pr-3">Last 4</th>
                    <th className="py-1 pr-3">Institution</th>
                    <th className="py-1 pr-3 text-right">Latest balance</th>
                    <th className="py-1 pr-3">Last seen</th>
                    {canEdit && <th className="py-1 pr-3">Link to FRT account</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <UnmappedRow
                      key={r.tiller_account_id}
                      row={r}
                      appAccounts={accountsList}
                      canEdit={canEdit}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function UnmappedRow({
  row,
  appAccounts,
  canEdit,
}: {
  row: UnmappedTillerAccount;
  appAccounts: AccountListItem[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  // Promote app accounts whose last4 matches this Tiller row's last4 to the
  // top of the picker, with a "(last4 match)" badge. All other accounts
  // remain selectable below — last4 is a hint, not a hard filter, because
  // Tiller occasionally truncates differently than the bank statement.
  const sortedOptions = useMemo(() => {
    const last4 = row.account_number_last4;
    if (!last4) return [...appAccounts].sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    );
    const matches: AccountListItem[] = [];
    const others: AccountListItem[] = [];
    for (const a of appAccounts) {
      if (a.account_number_last4 === last4) matches.push(a);
      else others.push(a);
    }
    matches.sort((a, b) => a.display_name.localeCompare(b.display_name));
    others.sort((a, b) => a.display_name.localeCompare(b.display_name));
    return [...matches, ...others];
  }, [appAccounts, row.account_number_last4]);

  const matchSet = useMemo(() => {
    if (!row.account_number_last4) return new Set<number>();
    return new Set(
      appAccounts
        .filter((a) => a.account_number_last4 === row.account_number_last4)
        .map((a) => a.id),
    );
  }, [appAccounts, row.account_number_last4]);

  const link = useMutation({
    mutationFn: (accountId: number) =>
      accountsApi.linkTiller(accountId, {
        tiller_account_id: row.tiller_account_id,
        tiller_account_reference: row.tiller_account_reference ?? null,
        reason: 'initial_link',
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['tiller', 'unmapped'] });
      void queryClient.invalidateQueries({ queryKey: ['mapping'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setError(msg);
    },
  });

  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="py-1 pr-3">
        <div className="font-medium text-slate-900">
          {row.tiller_account_reference ?? '(unnamed)'}
        </div>
        <div className="font-mono text-xs text-slate-500">{row.tiller_account_id}</div>
      </td>
      <td className="py-1 pr-3 font-mono text-xs">
        {row.account_number_last4 ?? '—'}
      </td>
      <td className="py-1 pr-3 text-slate-700">{row.institution ?? '—'}</td>
      <td className="py-1 pr-3 text-right font-mono">
        {formatBalance(row.latest_balance)}
      </td>
      <td className="py-1 pr-3 text-xs text-slate-600">
        {row.last_seen_uploaded_at?.slice(0, 10) ?? '—'}
      </td>
      {canEdit && (
        <td className="py-1 pr-3">
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded border border-slate-300 bg-white px-2 py-1 text-sm max-w-xs"
              value={target}
              onChange={(e) => setTarget(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">— pick FRT account —</option>
              {sortedOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name}
                  {matchSet.has(a.id) ? ' (last4 match)' : ''}
                  {a.tiller_account_id ? ' · already linked' : ''}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={() => target !== '' && link.mutate(target)}
              disabled={target === '' || link.isPending}
            >
              {link.isPending ? 'Linking…' : 'Link'}
            </Button>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        </td>
      )}
    </tr>
  );
}
